import Icons from '@/components/shared/Icons';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useFacebookToken } from '@/hooks/useFacebookToken';
import { useTeamStatus } from '@/hooks/useTeamStatus';
import {
  getActiveTeamQueryKey,
  getFacebookAccountsQueryKey,
} from '@/utils/query-keys';
import { QueryObserver, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

export const AccountConnectionsPage = () => {
  const { teamSocialsConnected } = useTeamStatus();
  const { disconnect } = useFacebookToken();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isDisconnecting, setIsDisconnecting] = useState(false);

  const handleDisconnect = async () => {
    try {
      setIsDisconnecting(true);
      await disconnect.mutateAsync();

      await new Promise<void>((resolve) => {
        const observer = new QueryObserver(queryClient, {
          queryKey: [getFacebookAccountsQueryKey(), getActiveTeamQueryKey()],
        });

        const unsubscribe = observer.subscribe((result) => {
          if (!result.isFetching) {
            unsubscribe();
            resolve();
          }
        });
      });

      navigate('/');
      toast.success('Successfully disconnected Instagram account');
      setIsDisconnecting(false);
    } catch (error) {
      setIsDisconnecting(false);
      console.error('Failed to disconnect Instagram:', error);
      toast.error('Failed to disconnect Instagram account. Please try again.');
    }
  };

  return (
    <div className="space-y-6">
      <Card className="flex items-center justify-between p-5">
        <div className="flex items-center gap-4">
          <div className="w-10 h-10">
            <Icons.instagram />
          </div>
          <div>
            <h3 className="font-semibold">Instagram</h3>
            {teamSocialsConnected && (
              <p className="text-sm text-green-600">
                ✓ Your Instagram account is connected
              </p>
            )}
          </div>
        </div>
        <Button
          variant={teamSocialsConnected ? 'outline' : 'default'}
          onClick={handleDisconnect}
          isLoading={isDisconnecting}>
          {teamSocialsConnected ? 'Disconnect' : 'Connect'}
        </Button>
      </Card>
    </div>
  );
};
